import COS from 'cos-js-sdk-v5';

import { TIMEOUT } from '@/utils/const';
import {
  ErrorType,
  ITokenData,
  IUploaderProps,
  IUploadInstanceOptions,
} from '@/utils/Uploader/type';

import { UploaderBase } from './base';

export class CosUploader implements UploaderBase {
  TIMEOUT: number = TIMEOUT;
  uploadId = '';
  public client: COS | null = null;
  private options: IUploaderProps | null = null;

  constructor(private callbackOptions: IUploadInstanceOptions) {}

  // 上传
  public async upload(options: IUploaderProps): Promise<string | null> {
    return new Promise(async (resolve) => {
      // 回调函数
      const { onError, onBefore } = this.callbackOptions;

      const { tokenData, file, fileUid } = options;
      this.options = options;

      const fileId = fileUid || '';
      const start = new Date().getTime();
      try {
        // 获取COS-Options参数
        const cosOptions = this.getCosOptions(tokenData);
        if (!cosOptions) {
          const err = new Error('Get COS Options Error');
          err.name = ErrorType.Other;
          throw err;
        }
        // 获取client
        this.client = this.getClient(cosOptions);
        if (!this.client) {
          const err = new Error('Get COS Client Error');
          err.name = ErrorType.Other;
          throw err;
        }

        // 开始上传前的回调
        try {
          onBefore && (await onBefore({ file }));
        } catch (error) {
          console.error(error);
          resolve(null);
          return;
        }

        const res = await this.multipartUpload(options);
        resolve(res);
      } catch (error: any) {
        const err = new Error(error?.message || '');
        err.name = error?.name || ErrorType.Other;
        onError &&
          onError(
            {
              error: err,
              name: error?.name || ErrorType.Other,
              message: error?.message || '',
              start,
              end: new Date().getTime(),
            },
            fileId,
            this,
          );
        resolve(null);
      }
    });
  }

  // 批量上传
  private async multipartUpload(options: IUploaderProps): Promise<string | null> {
    return new Promise(async (resolve, reject) => {
      const { tokenData } = options;
      if (this.client && tokenData) {
        const { onProgress, onSuccess, onStart } = this.callbackOptions;
        const { file } = options;
        const { filePath, accessUrl, bucket, region } = tokenData;
        const start = new Date().getTime();
        onStart && (await onStart(start));
        this.client.uploadFile(
          {
            Bucket: bucket,
            Region: region,
            Key: filePath,
            Body: file, // 上传文件对象
            SliceSize: 1024 * 1024 * 4,
            onTaskReady: (TaskId: COS.TaskId) => {
              this.uploadId = TaskId;
            },
            onProgress: async (progressData) => {
              const { percent } = progressData;
              onProgress &&
                (await onProgress({
                  progress: percent,
                  status: percent >= 1,
                  url: accessUrl,
                }));
            },
          },
          async (err: any, data) => {
            if (err) {
              reject(null);
            } else {
              onSuccess &&
                (await onSuccess(accessUrl, {
                  start,
                  end: new Date().getTime(),
                }));
              resolve(tokenData?.accessUrl || '');
            }
          },
        );
      }
    });
  }

  // 取消上传
  public async cancel() {
    if (this.client && this.uploadId) {
      try {
        this.client.cancelTask(this.uploadId);
        return true;
      } catch (error) {
        console.error(error);
      }
    }
    return false;
  }

  // 重试
  public async retry() {
    const options = this.options;
    if (options) {
      const { fileUid } = options;
      const start = new Date().getTime();
      try {
        return await this.multipartUpload(options);
      } catch (error: any) {
        console.error(error);
        const { onError } = this.callbackOptions;
        const newErr = new Error(error?.message || '');
        newErr.name = error?.name || ErrorType.Other;
        const err = {
          name: error.name || ErrorType.Other,
          message: error.message || '',
          start,
          end: new Date().getTime(),
          error: newErr,
        };
        onError && (await onError(err, fileUid || '', this));
        return null;
      }
    }
    return null;
  }

  // 获取oss-option
  private getCosOptions(tokenData: ITokenData): COS.COSOptions | null {
    if (!tokenData) return null;

    const { uploadHost, bucket, region, accessKey, accessSecret, securityToken } =
      tokenData;

    const { host } = new URL(uploadHost);

    const { timeout } = this.callbackOptions;

    return {
      Timeout: timeout || TIMEOUT,
      //   endpoint: `https://${bucket}.${host}`,
      //   region,
      Protocol: 'https:',
      Domain: `${bucket}.cos.${region}.myqcloud.com`,
      SecretKey: accessSecret,
      SecretId: accessKey,
      SecurityToken: securityToken,
      //   bucket,
      //   cname: true,
    };
  }

  public getClient(params: COS.COSOptions): COS {
    return new COS(params);
  }
}
